import { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMediaQuery } from './components/useMediaQuery'
import { layoutSizes } from './components/styled'
import { LetterItem } from './LetterItem'
import { AdaptiveTitleContainer, ComplexString, LettersString } from './styled'

const aBrakepoints = {
    xl: layoutSizes.elementWidth.isHorizontal.xl,
    lg: layoutSizes.elementWidth.isHorizontal.l,
    m: layoutSizes.elementWidth.isHorizontal.m,
}

const abcBrakepoints = {
    xxl: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    xl: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    lg: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    md: {
        a: aBrakepoints.m,
        b: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m / 2,
        c: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m
    },
    sm: {
        a: aBrakepoints.m,
        b: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m / 2,
        c: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m
    },
    xsm: { a: 0, b: 0, c: 0 },
}

const fontSizes = {
    xxl: 60,
    xl: 60,
    lg: 60,
    md: 50,
    sm: 40,
    xsm: 40,
}

const lowerRateLimits = { a: 0.4, b: 0.03, c: 0.07}
const HIGHER_RATE_LIMIT = 0.6
const EXTENDED_HIGHER_RATE_LIMIT = 0.68
const MAX_ITERATIONS = 12

const getRate = (props: any) => {
    const { containerWidth, titleWidth, title, fontSize} = props
    return Math.round((containerWidth - titleWidth) * 100 / title.length / fontSize) / 100
}

const getAbcRates = (props: any) => {
    const { abc } = props
    return { 
        aRate: getRate({ containerWidth: abc?.a, ...props }), 
        bRate: getRate({ containerWidth: abc?.b, ...props }), 
        cRate: getRate({ containerWidth: abc?.c, ...props }),
    }
}

const calculateWidth = (title: string, sizesInfo: any) => {
    let lettersWidth = 0
    const titleArr = title?.split('')
    titleArr?.forEach((letter: string) => {
        lettersWidth += sizesInfo[letter]
    })
    return lettersWidth
}

export const AdaptiveTitle: FC<{ title: string, style?: any, showTeamLabel?: boolean, onClick?: () => void }> = memo((props) => {
    const { title: titleSrc, showTeamLabel } = props
    const containerRef = useRef<HTMLDivElement>(null)

    const [sizesInfo, setSizes] = useState<any>({})
    const [multiRowText, setMultiRowText] = useState([titleSrc])
    const [stringSpacing, setStringSpacing] = useState<number | undefined>(undefined)
    const [isFixedMarginMode, setIsFixedMarginMode] = useState<boolean>(false)
    const [usedBreakPoint, setUsedBreakPoint] = useState<number | undefined>(undefined)
    const [isReady, setIsReady] = useState(false)
    const [isComplex, setIsComplex] = useState(false)
    const { breakPoint } = useMediaQuery()
    const fontSize = fontSizes[breakPoint]
    const abc = abcBrakepoints[breakPoint]

    useEffect(() => {
        setIsReady(false)
        setStringSpacing(undefined)
        setMultiRowText([titleSrc])
        setIsComplex(false)
    }, [titleSrc, breakPoint])

    // @ts-ignore
    const onLetterChange = useCallback(props => {
        const {idx, width, letter} = props 
        
        setSizes((prev: any) => {
            const newSizes = {...prev}
            newSizes[letter] = width
            return newSizes
        })
    }, [])

    const isNotFilled = useMemo(() => {
        //проверка все ли буквы загружены
        let notSizesFilled = false
        const titleArr = titleSrc?.split('')
        titleArr?.forEach((letter: string) => {
            if(!sizesInfo[letter]) {
                notSizesFilled = true
            }
        }, false)
        return notSizesFilled
    }, [sizesInfo, titleSrc])

    const titleWidth = useMemo(() => {
        //суммарная ширина букв заголовка
        return calculateWidth(titleSrc, sizesInfo)
    }, [titleSrc, sizesInfo])

    useLayoutEffect(() => {
        const title = ((multiRowText?.length === 1) && (multiRowText[0] === titleSrc)) 
            ? multiRowText[0] 
            : ''
        if((multiRowText?.length > 1) && isReady) {
            setIsReady(true)
            setUsedBreakPoint(abc?.c)
            setStringSpacing(lowerRateLimits.c)
            setIsComplex(true)
            return
        }
        if(!title?.length || isNotFilled) {
            setUsedBreakPoint(abc?.a)
            return
        }

        const { aRate, bRate, cRate } = getAbcRates({ abc, titleWidth, title, fontSize })

        const exceptCollection = title.replace('Collection', '').trim()
        const highLimit = (exceptCollection?.length === 1) ? EXTENDED_HIGHER_RATE_LIMIT : HIGHER_RATE_LIMIT
        if((aRate > highLimit)) {
            setIsFixedMarginMode(true)
            setUsedBreakPoint(abc?.a)
            return
        }
        if((lowerRateLimits.a <= aRate) && (aRate <= highLimit)) {
            setUsedBreakPoint(abc?.a)
            setStringSpacing(aRate)
            return
        }
        if((lowerRateLimits.b <= bRate) && (bRate <= highLimit)) {
            setUsedBreakPoint(abc?.b)
            setStringSpacing(bRate)
            return
        }
        if((lowerRateLimits.c <= cRate) && (cRate <= highLimit)) {
            setUsedBreakPoint(abc?.c)
            setStringSpacing(cRate)
            return
        }
        if(lowerRateLimits.c > cRate) {
            let isMatch = false
            let i = 0
            let row1Arr: string[] = title?.split(/[ ]/)          
            let row2Arr: string[] = []
            while (!((i > MAX_ITERATIONS) || isMatch)) {
                row2Arr = row2Arr.concat(row1Arr.slice(-1))
                row1Arr = row1Arr.slice(0, -1)

                const row2ArrStr = row2Arr.join('')
                const row1Title = title?.slice(0, -row2ArrStr?.length - row2Arr?.length + 1)?.trim()
                const row2Title = title?.slice(-row2ArrStr?.length - row2Arr?.length + 1)?.trim()
                const row1Width = calculateWidth(row1Title, sizesInfo);
                const row2Width = calculateWidth(row2Title, sizesInfo);

                const { aRate, bRate, cRate } = getAbcRates({ abc, titleWidth: row1Width, title: row1Title, fontSize })
                
                if((lowerRateLimits.a <= aRate) && (aRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.a)
                    setStringSpacing(aRate)
                    isMatch = true
                    break
                }
                if((lowerRateLimits.b <= bRate) && (bRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.b)
                    setStringSpacing(bRate)
                    isMatch = true
                    break
                    // if (row2Width > abc.c) {
                    //     const strs = row2Arr?.length > 1 ? row2Arr.reverse(): [row2Title]
                    //     setIsReady(() => true)
                    //     setMultiRowText([row1Title, ...strs])
                    //     setUsedBreakPoint(abc?.b)
                    //     setStringSpacing(bRate)
                    //     isMatch = true
                    //     break
                    // } else {
                    //     setIsReady(() => true)
                    //     setMultiRowText([row1Title, row2Title])
                    //     setUsedBreakPoint(abc?.b)
                    //     setStringSpacing(bRate)
                    //     isMatch = true
                    //     break
                    // }
                }
                if((lowerRateLimits.c <= cRate) && (cRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.c)
                    setStringSpacing(cRate)
                    isMatch = true
                    break
                }
                if((i + 1) === MAX_ITERATIONS ) {
                    setIsReady(true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.c)
                    setStringSpacing(lowerRateLimits.c)
                    setIsComplex(true)
                    isMatch = true
                }
                i+=1;
            }
            return
        }
        setUsedBreakPoint(abc?.a)
    }, [sizesInfo, titleSrc, multiRowText, breakPoint, titleWidth, isNotFilled, abc, isReady, fontSize])
    
    const lettersArray = useMemo(() => {
        const fullTitleArray = titleSrc?.length 
                ? titleSrc.split('') : undefined
        return multiRowText?.length === 1
            ? fullTitleArray?.length
                ? fullTitleArray.map((letter: string, idx: number) => (
                    <LetterItem 
                        key={idx}
                        idx={idx} 
                        letter={letter} 
                        onLetterChange={onLetterChange} 
                    /> 
                ))
                : ''
            : multiRowText?.length 
                ? multiRowText.map((rowTitle: string, stringId: number) => {
                    const titleArray = rowTitle?.length 
                        ? rowTitle.split('') : undefined;
                    const justify = multiRowText?.length > 2 ? (stringId > 0 ? undefined : 'space-between') : stringId !== (multiRowText?.length - 1)
                        ? 'space-between' : undefined;
                    const spacing = (multiRowText?.length > 2 && stringId !== 0) ? stringSpacing : !justify ? stringSpacing : undefined;

                    return <LettersString 
                        justify={justify}
                        {...(multiRowText?.length > 2 ? { w: 'auto'} : {})}
                        letterSpacing={spacing}
                    >
                        {
                            titleArray?.length 
                                ? isComplex
                                    ? <ComplexString
                                        letterSpacing={stringSpacing}
                                    > 
                                        {rowTitle}
                                    </ComplexString>
                                    : titleArray.map((letter: string, idx: number) => (
                                        <LetterItem 
                                            key={idx}
                                            idx={idx} 
                                            letter={letter} 
                                            onLetterChange={onLetterChange} 
                                        /> 
                                    ))
                                : ''
                        }
                    </LettersString>
                })     
                : ''
    }, [titleSrc, multiRowText, isComplex, stringSpacing, onLetterChange])
    const usedBreakPointWithTeamLabel = useMemo(() => usedBreakPoint && usedBreakPoint - 30, [usedBreakPoint, showTeamLabel])
    
    return <AdaptiveTitleContainer 
            ref={containerRef}
            justify={isFixedMarginMode ? 'flex-start' : 'space-between'}
            columnGap={isFixedMarginMode ? 35 : 0}
            usedBreakPoint={showTeamLabel ? usedBreakPointWithTeamLabel : usedBreakPoint}
            useWrap={(multiRowText?.length > 1) && isReady}
            fontSize={fontSize}
            onClick={props.onClick}
            style={props.style}
        >
            { lettersArray }
        </AdaptiveTitleContainer>
})
import { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMediaQuery } from './components/useMediaQuery'
import { layoutSizes } from './components/styled'
import { LetterItem } from './LetterItem'
import { AdaptiveTitleContainer, ComplexString, LettersString } from './styled'

const aBrakepoints = {
    xl: layoutSizes.elementWidth.isHorizontal.xl,
    lg: layoutSizes.elementWidth.isHorizontal.l,
    m: layoutSizes.elementWidth.isHorizontal.m,
}

const abcBrakepoints = {
    xxl: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    xl: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    lg: {
        a: aBrakepoints.xl,
        b: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl / 2,
        c: aBrakepoints.xl + layoutSizes.grid.distance.xl + layoutSizes.museumWidth.xl
    },
    md: {
        a: aBrakepoints.m,
        b: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m / 2,
        c: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m
    },
    sm: {
        a: aBrakepoints.m,
        b: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m / 2,
        c: aBrakepoints.m + layoutSizes.grid.distance.m + layoutSizes.museumWidth.m
    },
    xsm: { a: 0, b: 0, c: 0 },
}

const fontSizes = {
    xxl: 60,
    xl: 60,
    lg: 60,
    md: 50,
    sm: 40,
    xsm: 40,
}

const lowerRateLimits = { a: 0.4, b: 0.03, c: 0.07}
const HIGHER_RATE_LIMIT = 0.6
const EXTENDED_HIGHER_RATE_LIMIT = 0.68
const MAX_ITERATIONS = 12

const getRate = (props: any) => {
    const { containerWidth, titleWidth, title, fontSize} = props
    return Math.round((containerWidth - titleWidth) * 100 / title.length / fontSize) / 100
}

const getAbcRates = (props: any) => {
    const { abc } = props
    return { 
        aRate: getRate({ containerWidth: abc?.a, ...props }), 
        bRate: getRate({ containerWidth: abc?.b, ...props }), 
        cRate: getRate({ containerWidth: abc?.c, ...props }),
    }
}

const calculateWidth = (title: string, sizesInfo: any) => {
    let lettersWidth = 0
    const titleArr = title?.split('')
    titleArr?.forEach((letter: string) => {
        lettersWidth += sizesInfo[letter]
    })
    return lettersWidth
}

export const AdaptiveTitle: FC<{ title: string, style?: any, showTeamLabel?: boolean, onClick?: () => void }> = memo((props) => {
    const { title: titleSrc, showTeamLabel } = props
    const containerRef = useRef<HTMLDivElement>(null)

    const [sizesInfo, setSizes] = useState<any>({})
    const [multiRowText, setMultiRowText] = useState([titleSrc])
    const [stringSpacing, setStringSpacing] = useState<number | undefined>(undefined)
    const [isFixedMarginMode, setIsFixedMarginMode] = useState<boolean>(false)
    const [usedBreakPoint, setUsedBreakPoint] = useState<number | undefined>(undefined)
    const [isReady, setIsReady] = useState(false)
    const [isComplex, setIsComplex] = useState(false)
    const { breakPoint } = useMediaQuery()
    const fontSize = fontSizes[breakPoint]
    const abc = abcBrakepoints[breakPoint]

    useEffect(() => {
        setIsReady(false)
        setStringSpacing(undefined)
        setMultiRowText([titleSrc])
        setIsComplex(false)
    }, [titleSrc, breakPoint])

    // @ts-ignore
    const onLetterChange = useCallback(props => {
        const {idx, width, letter} = props 
        
        setSizes((prev: any) => {
            const newSizes = {...prev}
            newSizes[letter] = width
            return newSizes
        })
    }, [])

    const isNotFilled = useMemo(() => {
        //проверка все ли буквы загружены
        let notSizesFilled = false
        const titleArr = titleSrc?.split('')
        titleArr?.forEach((letter: string) => {
            if(!sizesInfo[letter]) {
                notSizesFilled = true
            }
        }, false)
        return notSizesFilled
    }, [sizesInfo, titleSrc])

    const titleWidth = useMemo(() => {
        //суммарная ширина букв заголовка
        return calculateWidth(titleSrc, sizesInfo)
    }, [titleSrc, sizesInfo])

    useLayoutEffect(() => {
        const title = ((multiRowText?.length === 1) && (multiRowText[0] === titleSrc)) 
            ? multiRowText[0] 
            : ''
        if((multiRowText?.length > 1) && isReady) {
            setIsReady(true)
            setUsedBreakPoint(abc?.c)
            setStringSpacing(lowerRateLimits.c)
            setIsComplex(true)
            return
        }
        if(!title?.length || isNotFilled) {
            setUsedBreakPoint(abc?.a)
            return
        }

        const { aRate, bRate, cRate } = getAbcRates({ abc, titleWidth, title, fontSize })

        const exceptCollection = title.replace('Collection', '').trim()
        const highLimit = (exceptCollection?.length === 1) ? EXTENDED_HIGHER_RATE_LIMIT : HIGHER_RATE_LIMIT
        if((aRate > highLimit)) {
            setIsFixedMarginMode(true)
            setUsedBreakPoint(abc?.a)
            return
        }
        if((lowerRateLimits.a <= aRate) && (aRate <= highLimit)) {
            setUsedBreakPoint(abc?.a)
            setStringSpacing(aRate)
            return
        }
        if((lowerRateLimits.b <= bRate) && (bRate <= highLimit)) {
            setUsedBreakPoint(abc?.b)
            setStringSpacing(bRate)
            return
        }
        if((lowerRateLimits.c <= cRate) && (cRate <= highLimit)) {
            setUsedBreakPoint(abc?.c)
            setStringSpacing(cRate)
            return
        }
        if(lowerRateLimits.c > cRate) {
            let isMatch = false
            let i = 0
            let row1Arr: string[] = title?.split(/[ ]/)          
            let row2Arr: string[] = []
            while (!((i > MAX_ITERATIONS) || isMatch)) {
                row2Arr = row2Arr.concat(row1Arr.slice(-1))
                row1Arr = row1Arr.slice(0, -1)

                const row2ArrStr = row2Arr.join('')
                const row1Title = title?.slice(0, -row2ArrStr?.length - row2Arr?.length + 1)?.trim()
                const row2Title = title?.slice(-row2ArrStr?.length - row2Arr?.length + 1)?.trim()
                const row1Width = calculateWidth(row1Title, sizesInfo);
                const row2Width = calculateWidth(row2Title, sizesInfo);

                const { aRate, bRate, cRate } = getAbcRates({ abc, titleWidth: row1Width, title: row1Title, fontSize })
                
                if((lowerRateLimits.a <= aRate) && (aRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.a)
                    setStringSpacing(aRate)
                    isMatch = true
                    break
                }
                if((lowerRateLimits.b <= bRate) && (bRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.b)
                    setStringSpacing(bRate)
                    isMatch = true
                    break
                    // if (row2Width > abc.c) {
                    //     const strs = row2Arr?.length > 1 ? row2Arr.reverse(): [row2Title]
                    //     setIsReady(() => true)
                    //     setMultiRowText([row1Title, ...strs])
                    //     setUsedBreakPoint(abc?.b)
                    //     setStringSpacing(bRate)
                    //     isMatch = true
                    //     break
                    // } else {
                    //     setIsReady(() => true)
                    //     setMultiRowText([row1Title, row2Title])
                    //     setUsedBreakPoint(abc?.b)
                    //     setStringSpacing(bRate)
                    //     isMatch = true
                    //     break
                    // }
                }
                if((lowerRateLimits.c <= cRate) && (cRate <= highLimit)) {
                    setIsReady(() => true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.c)
                    setStringSpacing(cRate)
                    isMatch = true
                    break
                }
                if((i + 1) === MAX_ITERATIONS ) {
                    setIsReady(true)
                    setMultiRowText([row1Title, row2Title])
                    setUsedBreakPoint(abc?.c)
                    setStringSpacing(lowerRateLimits.c)
                    setIsComplex(true)
                    isMatch = true
                }
                i+=1;
            }
            return
        }
        setUsedBreakPoint(abc?.a)
    }, [sizesInfo, titleSrc, multiRowText, breakPoint, titleWidth, isNotFilled, abc, isReady, fontSize])
    
    const lettersArray = useMemo(() => {
        const fullTitleArray = titleSrc?.length 
                ? titleSrc.split('') : undefined
        return multiRowText?.length === 1
            ? fullTitleArray?.length
                ? fullTitleArray.map((letter: string, idx: number) => (
                    <LetterItem 
                        key={idx}
                        idx={idx} 
                        letter={letter} 
                        onLetterChange={onLetterChange} 
                    /> 
                ))
                : ''
            : multiRowText?.length 
                ? multiRowText.map((rowTitle: string, stringId: number) => {
                    const titleArray = rowTitle?.length 
                        ? rowTitle.split('') : undefined;
                    const justify = multiRowText?.length > 2 ? (stringId > 0 ? undefined : 'space-between') : stringId !== (multiRowText?.length - 1)
                        ? 'space-between' : undefined;
                    const spacing = (multiRowText?.length > 2 && stringId !== 0) ? stringSpacing : !justify ? stringSpacing : undefined;

                    return <LettersString 
                        justify={justify}
                        {...(multiRowText?.length > 2 ? { w: 'auto'} : {})}
                        letterSpacing={spacing}
                    >
                        {
                            titleArray?.length 
                                ? isComplex
                                    ? <ComplexString
                                        letterSpacing={stringSpacing}
                                    > 
                                        {rowTitle}
                                    </ComplexString>
                                    : titleArray.map((letter: string, idx: number) => (
                                        <LetterItem 
                                            key={idx}
                                            idx={idx} 
                                            letter={letter} 
                                            onLetterChange={onLetterChange} 
                                        /> 
                                    ))
                                : ''
                        }
                    </LettersString>
                })     
                : ''
    }, [titleSrc, multiRowText, isComplex, stringSpacing, onLetterChange])
    const usedBreakPointWithTeamLabel = useMemo(() => usedBreakPoint && usedBreakPoint - 30, [usedBreakPoint, showTeamLabel])
    
    return <AdaptiveTitleContainer 
            ref={containerRef}
            justify={isFixedMarginMode ? 'flex-start' : 'space-between'}
            columnGap={isFixedMarginMode ? 35 : 0}
            usedBreakPoint={showTeamLabel ? usedBreakPointWithTeamLabel : usedBreakPoint}
            useWrap={(multiRowText?.length > 1) && isReady}
            fontSize={fontSize}
            onClick={props.onClick}
            style={props.style}
        >
            { lettersArray }
        </AdaptiveTitleContainer>
})
